import { Component } from '@theme/component';
import { SET_CHANGE_EVENT } from '@theme/lift-buybox';

/**
 * The sticky buy bar: a thumbnail, the price of the chosen set, and a button
 * that opens the buybox's sheet.
 *
 * The sheet is NOT here. It renders inside lift-buybox, because every label on
 * its set cards is a setting of that section and a section cannot read another
 * one's settings. The bar reaches it the way this theme already reaches a
 * nested component elsewhere — by selector and method call, as the buybox does
 * with `gallery.select()`.
 *
 * The bar carries no variant payload of its own. It learns about a new set
 * from lift:set-change, which hands over the strings the server already
 * formatted, so nothing here calculates or formats either.
 *
 * @typedef {object} Refs
 * @property {HTMLImageElement} [thumb]
 * @property {HTMLElement} [price]
 * @property {HTMLElement} [compareRow]
 * @property {HTMLElement} [compare]
 *
 * @extends {Component<Refs>}
 */
class LiftStickyBar extends Component {
  /** @type {IntersectionObserver|null} */
  #observer = null;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(SET_CHANGE_EVENT, this.#handleSetChange);
    this.#watchBuybox();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(SET_CHANGE_EVENT, this.#handleSetChange);
    this.#observer?.disconnect();
    this.#observer = null;
  }

  /** Called from on:click on the button. */
  handleOpen() {
    const sheet = document.querySelector('.lift-sheet-host');

    if (sheet && 'showDialog' in sheet && typeof sheet.showDialog === 'function') {
      sheet.showDialog();
    }
  }

  /**
   * The bar steps aside while the buybox is on screen — two buy buttons at
   * once is one too many. The prototype's threshold, and it holds as long as
   * 12% of the buybox is shorter than the viewport, which it is at every width
   * we ship.
   *
   * No buybox on the page means nothing to step aside for: the bar stays.
   */
  #watchBuybox() {
    const buybox = document.querySelector('.lift-buybox');
    if (!buybox) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          this.classList.toggle('lift-sticky-bar--hidden', entry.isIntersecting);
        }
      },
      { threshold: 0.12 }
    );

    this.#observer.observe(buybox);
  }

  /**
   * @param {Event} event
   */
  #handleSetChange = (event) => {
    const variant = /** @type {CustomEvent} */ (event).detail?.variant;
    if (!variant) return;

    const { thumb, price, compareRow, compare } = this.refs;

    if (price) price.textContent = variant.price;

    if (thumb instanceof HTMLImageElement && variant.mediaUrl) {
      // Without this the old srcset would keep winning over the new src.
      thumb.removeAttribute('srcset');
      thumb.src = variant.mediaUrl;
    }

    if (compareRow) {
      if (variant.compare) {
        if (compare) compare.textContent = variant.compare;
        compareRow.hidden = false;
      } else {
        compareRow.hidden = true;
      }
    }
  };
}

if (!customElements.get('lift-sticky-bar-component')) {
  customElements.define('lift-sticky-bar-component', LiftStickyBar);
}
